import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Pencil, Send, Trash2, Upload } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, hasPermission } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  DescriptionList,
  DocumentList,
  ErrorState,
  FileDropzone,
  HtmlFrame,
  Label,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { DetailSkeleton } from '../../../components/data.jsx';
import { SendDialog } from '../../../components/send-dialog.jsx';
import { AnnouncementStateBadge, PublishWindow } from '../common.jsx';
import { AnnouncementImageCard } from './image-card.jsx';

export function AnnouncementDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { announcementId } = useParams({ strict: false });
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data: announcement, error, isPending, refetch } = useQuery(adminQueries.announcement(api, announcementId));

  const breadcrumb = (
    <Link to="/kommunikation/mitteilungen" className="hover:underline">
      Mitteilungen
    </Link>
  );
  if (error) {
    return (
      <>
        <PageHeader title="Mitteilung" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Mitteilung nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const canManage = hasPermission(me, 'announcements.manage');
  const canSend = hasPermission(me, 'announcements.send');

  async function remove() {
    try {
      await api.admin.announcements.remove(announcementId);
      queryClient.removeQueries({ queryKey: keys.admin.detail('announcements', announcementId) });
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('announcements') });
      toast.success('Mitteilung gelöscht');
      navigate({ to: '/kommunikation/mitteilungen' });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Mitteilung konnte nicht gelöscht werden.'));
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={announcement.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <AnnouncementStateBadge announcement={announcement} />
            <PublishWindow from={announcement.publish_at} to={announcement.unpublish_at} />
          </span>
        }
        actions={
          <>
            {canManage && (
              <Button asChild size="sm" variant="outline">
                <Link to="/kommunikation/mitteilungen/$announcementId/bearbeiten" params={{ announcementId: String(announcement.id) }}>
                  <Pencil aria-hidden="true" /> Bearbeiten
                </Link>
              </Button>
            )}
            {canSend && (
              <Button size="sm" onClick={() => setDispatchOpen(true)}>
                <Send aria-hidden="true" /> Versenden
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)}>
                <Trash2 aria-hidden="true" /> Löschen
              </Button>
            )}
          </>
        }
      />
      <div className="grid gap-3 lg:grid-cols-[1fr_22rem]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle as="h2">Inhalt</CardTitle>
          </CardHeader>
          <CardContent>
            {announcement.body_html ? (
              <HtmlFrame html={announcement.body_html} title={`Inhalt ${announcement.title}`} colorScheme="auto" />
            ) : (
              <p className="text-sm text-muted-foreground">Kein Text.</p>
            )}
          </CardContent>
        </Card>
        <div className="flex flex-col gap-3">
          <Card>
            <CardHeader>
              <CardTitle as="h2">Angaben</CardTitle>
            </CardHeader>
            <CardContent>
              <DescriptionList
                labelWidth="8rem"
                items={[
                  { label: 'Veröffentlicht', value: announcement.is_published ? 'Ja' : 'Nein' },
                  { label: 'Sichtbar ab', value: announcement.publish_at ? formatDateTime(announcement.publish_at) : 'sofort' },
                  { label: 'Sichtbar bis', value: announcement.unpublish_at ? formatDateTime(announcement.unpublish_at) : 'unbegrenzt' },
                  { label: 'Erstellt', value: announcement.created_at ? formatDateTime(announcement.created_at) : null },
                  {
                    label: 'Liegenschaften',
                    value: announcement.properties?.length ? (
                      <ul className="flex flex-col">
                        {announcement.properties.map((p) => (
                          <li key={p.id}>
                            <Link to="/liegenschaften/$propertyId" params={{ propertyId: String(p.id) }} className="text-primary hover:underline">
                              {p.name || `#${p.id}`}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : null,
                  },
                ]}
              />
            </CardContent>
          </Card>
          <AnnouncementImageCard announcement={announcement} canManage={canManage} />
          <AttachmentsCard announcement={announcement} />
        </div>
      </div>
      <DispatchHistory dispatches={announcement.dispatches ?? []} />

      {canSend && (
        <DispatchDialog
          announcement={announcement}
          open={dispatchOpen}
          onOpenChange={setDispatchOpen}
          onSent={() => queryClient.invalidateQueries({ queryKey: keys.admin.resource('announcements') })}
        />
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Mitteilung löschen?"
        description="Die Mitteilung verschwindet aus dem Portal. Anhänge bleiben als Dokumente erhalten."
        confirmLabel="Löschen"
        destructive
        onConfirm={remove}
      />
    </>
  );
}

function AttachmentsCard({ announcement }) {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const canUpload = hasPermission(me, 'documents.upload') && hasPermission(me, 'announcements.manage') && announcement.attachment_folder_id;
  const upload = useMutation({
    mutationFn: async (list) => {
      for (const file of list) await api.admin.documentFolders.upload(announcement.attachment_folder_id, { file });
    },
    onSuccess: () => {
      toast.success(files.length === 1 ? 'Anhang hochgeladen' : `${files.length} Anhänge hochgeladen`);
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: keys.admin.detail('announcements', announcement.id) });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Der Upload ist fehlgeschlagen.')),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Anhänge</CardTitle>
        <CardDescription>Werden beim Versand optional mitgeschickt und sind im Portal abrufbar.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <DocumentList
          label="Anhänge"
          documents={announcement.attachments}
          hrefFor={(doc) => api.admin.documents.downloadUrl(doc.id)}
          emptyText="Keine Anhänge."
        />
        {canUpload && (
          <>
            <FileDropzone files={files} onFilesChange={setFiles} label="Anhänge" hint="max. 20 MB je Datei" disabled={upload.isPending} />
            {files.length > 0 && (
              <Button size="sm" className="w-fit" loading={upload.isPending} onClick={() => upload.mutate(files)}>
                <Upload aria-hidden="true" /> Hochladen
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DispatchHistory({ dispatches }) {
  return (
    <section aria-labelledby="dispatch-history" className="mt-4 flex flex-col gap-2">
      <h2 id="dispatch-history" className="text-h3 font-semibold">
        Versandhistorie
      </h2>
      {dispatches.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch nicht versendet.</p>
      ) : (
        <Table aria-label="Versandläufe" containerClassName="rounded-md border">
          <TableHeader>
            <TableRow>
              <TableHead>Eingereiht</TableHead>
              <TableHead>Status</TableHead>
              <TableHead data-numeric="true">Empfänger</TableHead>
              <TableHead>Anhänge</TableHead>
              <TableHead>Beendet</TableHead>
              <TableHead>Fehler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dispatches.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="numeric">{formatDateTime(d.created_at)}</TableCell>
                <TableCell>
                  <StatusBadge domain="announcementDispatch" status={d.status} />
                </TableCell>
                <TableCell data-numeric="true">{d.recipients_count == null ? '–' : formatNumber(d.recipients_count)}</TableCell>
                <TableCell>{d.with_attachments ? 'mit Anhängen' : 'ohne'}</TableCell>
                <TableCell className="numeric">{formatDateTime(d.finished_at)}</TableCell>
                <TableCell className="text-xs text-destructive">{d.error ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

/** Recipients preview (GET …/recipients) → confirm → POST …/dispatch; 409 already-dispatched → explicit resend. */
function DispatchDialog({ announcement, open, onOpenChange, onSent }) {
  const api = useApi();
  const [withAttachments, setWithAttachments] = useState(true);
  return (
    <SendDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Mitteilung versenden"
      description={`„${announcement.title}“ wird per E-Mail an die Eigentümer der gewählten Liegenschaften versendet (Warteschlange).`}
      confirmLabel="Jetzt versenden"
      send={({ resend }) => api.admin.announcements.dispatch(announcement.id, { with_attachments: withAttachments, resend })}
      onSent={(_result, { resend }) => {
        toast.success(resend ? 'Erneuter Versand eingereiht' : 'Versand eingereiht');
        onSent?.();
      }}
    >
      <RecipientsPreview announcementId={announcement.id} />
      {announcement.attachments?.length > 0 && (
        <div className="flex items-center gap-2">
          <Checkbox id="dispatch-with-attachments" checked={withAttachments} onChange={(e) => setWithAttachments(e.target.checked)} />
          <Label htmlFor="dispatch-with-attachments" className="font-normal">
            Anhänge mitsenden ({announcement.attachments.length})
          </Label>
        </div>
      )}
    </SendDialog>
  );
}

function RecipientsPreview({ announcementId }) {
  const api = useApi();
  const { data, error, isPending } = useQuery(adminQueries.announcementRecipients(api, announcementId));
  if (isPending) return <Skeleton className="h-16 w-full" aria-label="Empfänger werden geladen" />;
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{getErrorMessage(error, 'Die Empfänger konnten nicht ermittelt werden.')}</AlertDescription>
      </Alert>
    );
  }
  const users = data.portal_users ?? [];
  const emails = data.email_only ?? [];
  return (
    <section aria-label="Empfänger" className="flex flex-col gap-2 text-sm">
      <p>
        Empfänger: <strong className="numeric">{formatNumber(data.count)}</strong>
      </p>
      {data.count === 0 && <p className="text-muted-foreground">Für die gewählten Liegenschaften gibt es keine Empfänger.</p>}
      {(users.length > 0 || emails.length > 0) && (
        <ul aria-label="Empfängerliste" className="max-h-48 overflow-y-auto rounded-md border px-3 py-2 text-xs">
          {users.map((u) => (
            <li key={`u-${u.id}`}>
              {u.name} <span className="text-muted-foreground">&lt;{u.email}&gt;</span>
            </li>
          ))}
          {emails.map((e) => (
            <li key={`e-${e}`}>
              {e} <span className="text-muted-foreground">(nur E-Mail)</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
